import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@vd/ui';
import { placeBox, type Placement, type Rect } from './placement';
import type { TourDefinition } from './tours';
import { TourArt } from './TourArt';
import { modalOpen, topmostModal } from './modalOpen';
import styles from './TourSpotlight.module.css';

/** How long a step waits for its control to appear, or come back, before saying it is not on the page. */
export const ANCHOR_WAIT_MS = 5_000;

interface TourSpotlightProps {
  tour: TourDefinition;
  step: number;
  /** Set while the person is outside the workspace or project the tour was started in. */
  elsewhere: TourDefinition['scope'] | null;
  /** Set while a recording is under way: the tour pauses, pointing at nothing the person should reach for. */
  recording: boolean;
  /** On a ticket whose project is not known yet: a page wait is not met until it is (TOADO-988). */
  scopePending: boolean;
  /** On a ticket that could not be loaded: its steps cannot be shown (review round 7). */
  ticketFailed: boolean;
  /** Set while a modal dialog is open: the tour is out of sight altogether. */
  underModal: boolean;
  /** The address of this step's page, where the tour knows it: what a paused tour offers to go to (Jason, 28 Oct). */
  stepPage: string | null;
  /** Where the step is among the steps of this person's tour, which is what the box counts (Jason, 28 Oct). */
  position: { index: number; total: number };
  /** Whether there is a step before this one in this person's tour. */
  canGoBack: boolean;
  onBack(): void;
  onNext(): void;
  /** The whole tour, for now: offered again from Get started. */
  onSkip(): void;
  /** Past every step of this step's `section` (TOADO-988): what Skip step does inside one. */
  onSkipSection(): void;
  /** Closes it where it is, to resume at this step. */
  onClose(): void;
}

const anchorFor = (anchor: string) => document.querySelector<HTMLElement>(`[data-tour-anchor="${CSS.escape(anchor)}"]`);

/**
 * Clicks a control for the person, as Next does on a step that asks for a
 * click (Jason, 28 Oct): the control itself when it is one, or the first one
 * inside it. Not a trusted click, so a step's own click listener never takes
 * it for the person's.
 */
function press(control: HTMLElement): void {
  const clickable = control.matches('button, a, [role="button"], [role="tab"], summary, input')
    ? control
    : control.querySelector<HTMLElement>('button, a, [role="button"], [role="tab"]') ?? control;
  clickable.click();
}

/**
 * Whether a step's control is inside the topmost open modal, and whether it
 * is on the page at all (TOADO-988), kept current while `active`: the control
 * arrives with the ticket's data, after the modal has opened, another modal
 * can open over it, and its own window can close.
 */
type TopModal = { inside: boolean; present: boolean; seen: boolean; top: Element | null };
const NO_MODAL: TopModal = { inside: false, present: false, seen: false, top: null };

function useInTopModal(anchor: string, active: boolean): TopModal {
  const [where, setWhere] = useState<TopModal>(NO_MODAL);
  useEffect(() => {
    // A new control, or none to watch: nothing seen yet.
    setWhere((last) => (last.inside || last.present || last.seen || last.top ? NO_MODAL : last));
    if (!active) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const top = topmostModal();
      const control = anchorFor(anchor);
      const inside = !!top && !!control && top.contains(control);
      const present = !!control;
      // Seen once while active: a window still opening is not one that closed.
      // Seen in the window, not merely on the page: a copy of the control on
      // the page beneath says nothing about the window (review round 8).
      // And which window is on top, which the tour's copy follows when one
      // closes over another (review round 9).
      setWhere((last) => {
        const seen = last.seen || inside;
        return last.inside === inside && last.present === present && last.seen === seen && last.top === top
          ? last
          : { inside, present, seen, top };
      });
    };
    const soon = () => { if (!frame) frame = window.requestAnimationFrame(check); };
    const observer = new MutationObserver(soon);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    check();
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [anchor, active]);
  return where;
}

/**
 * One tour step on the page (TOADO-971): a ring around the control the step
 * is about and a box beside it saying what it is.
 *
 * Nothing covers the page: the ring lets clicks through and there is no
 * backdrop, so the person can always reach the control the step asks for. The
 * box is a non-modal dialog that takes focus on each step; Escape closes the
 * tour from the box or the page, unless a modal has it. A step whose control
 * is not there within ANCHOR_WAIT_MS, at first or after it has left, says so
 * and offers to skip it. On another route, or outside the workspace or
 * project the tour was started in, the tour pauses: it holds no control and
 * hears no action until the person is back.
 */
export function TourSpotlight({ tour, step, elsewhere, recording, scopePending, ticketFailed, underModal, stepPage, position, canGoBack, onBack, onNext, onSkip, onSkipSection, onClose }: TourSpotlightProps) {
  const current = tour.steps[step]!;
  const location = useLocation();
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef<HTMLDivElement>(null);
  /**
   * Whether the keyboard is on the tour's copy inside a window, and on which
   * button (review round 10). A step that moves on, into a pause or a new
   * window, takes that button away, and focus would fall to the page behind
   * the window; the copy that replaces it takes it back.
   */
  const keyboardInWindow = useRef<KeyboardInWindow>({ element: null, label: null });
  useEffect(() => {
    const noted = (event: FocusEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const on = !!target?.closest('[data-tour-said]');
      keyboardInWindow.current = on
        ? { element: target, label: target!.getAttribute('aria-label') ?? target!.textContent }
        : { element: null, label: null };
    };
    // A press anywhere outside the copy is the person choosing where focus goes,
    // even onto text that takes none: nothing is given back after it, even if the
    // step then moves on and takes the button away (review round 12).
    const pressed = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-tour-said]')) keyboardInWindow.current = { element: null, label: null };
    };
    document.addEventListener('focusin', noted);
    document.addEventListener('pointerdown', pressed, true);
    return () => {
      document.removeEventListener('focusin', noted);
      document.removeEventListener('pointerdown', pressed, true);
    };
  }, []);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [missing, setMissing] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [done, setDone] = useState(!current.waitFor);
  // Where the step was last shown, so a paused tour can go back there.
  const [shownAt, setShownAt] = useState<string | null>(null);
  const onRoute = !current.route || matchPath(current.route, location.pathname) !== null;
  // Over a modal it is out of sight altogether, unless its control is in the
  // topmost one (TOADO-988): a confirmation opened over the ticket hides it
  // again. During a recording it pauses.
  const modal = useInTopModal(current.anchor, underModal && current.overModal === true);
  // A centred step over a modal is about the window it sits over, whatever is on top (Jason, 28 Oct).
  const covered = underModal && !(current.centered && current.overModal) && !modal.inside;
  // On a ticket whose project is not known yet, it is out of sight until it is (TOADO-988).
  const showing = onRoute && !elsewhere && !recording && !covered && !scopePending;
  /**
   * A step in a window with another window open over it (Back from Session
   * to the ticket's comments, say): said rather than hidden, since nothing
   * else on the page tells the person where the tour went (TOADO-988).
   */
  const behindAnother = covered && onRoute && current.overModal === true && !scopePending && modal.present;
  /**
   * A step in a window that has been closed (Escape out of Session at its
   * step): its own words say how to open it again, rather than the tour
   * vanishing with Skip and Close (review round 6). Or one never seen open
   * since the step began (resumed at Session with the ticket open and Session
   * not, review round 8): waited for as long as any control is, so a window
   * still opening is not taken for a closed one, then said the same way.
   */
  const unopened = covered && onRoute && current.overModal === true && !scopePending && !modal.present;
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    setWaitedOut(false);
    if (!unopened) return;
    const timer = window.setTimeout(() => setWaitedOut(true), ANCHOR_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [unopened, tour.id, step]);
  const windowClosed = unopened && (modal.seen || waitedOut);
  // A ticket step on a ticket that failed to load: said, with Skip, never left blank (review round 7).
  const cannotLoad = onRoute && current.overModal === true && ticketFailed && !modal.present;
  // The window, so the box is placed again when it changes, though its control did not move.
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const resized = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', resized);
    return () => window.removeEventListener('resize', resized);
  }, []);

  // A new step starts over: its own control, its own wait.
  useEffect(() => {
    setTarget(null);
    setMissing(false);
    setDone(!current.waitFor);
    setShownAt(null);
  }, [tour.id, step, current.waitFor]);

  // After the reset above, so a step shown on the page it starts on records it.
  useEffect(() => {
    if (showing) setShownAt(location.pathname + location.search);
  }, [showing, location.pathname, location.search, tour.id, step]);

  // Find the control while the step is showing. Paused, it holds none, so a
  // control gone meanwhile is never ringed where it used to be. Missing at
  // first or gone later, it is waited for a bounded time, then said.
  useEffect(() => {
    setTarget(null);
    setMissing(false);
    // A centred step points at nothing, so nothing is looked for or missed.
    if (!showing || current.centered) return;
    let found: HTMLElement | null = null;
    let timer: number | undefined;
    const wait = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setMissing(true), ANCHOR_WAIT_MS);
    };
    const look = () => {
      const now = anchorFor(current.anchor);
      if (now === found) return;
      found = now;
      setTarget(now);
      if (now) {
        window.clearTimeout(timer);
        setMissing(false);
      } else {
        wait();
      }
    };
    look();
    if (!found) wait();
    const observer = new MutationObserver(look);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tour-anchor'] });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
    // The step too: two steps on one control each look for it after the reset.
  }, [showing, current.anchor, current.centered, tour.id, step]);

  /**
   * The name of the chosen option in the step's control, for a body that says
   * it (Jason, 28 Oct): read as the control changes, since a view picked the
   * moment before the step shows can be applied a render later.
   */
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    setSelected(null);
    if (!target || !current.body.includes('{selected}')) return;
    const read = () => setSelected(target.querySelector('[aria-selected="true"]')?.textContent?.trim() || null);
    read();
    const observer = new MutationObserver(read);
    observer.observe(target, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
    return () => observer.disconnect();
  }, [target, current.body]);

  // Into view once, as the control is found.
  useEffect(() => {
    if (!target) return;
    const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
  }, [target]);

  // Kept beside the control as the page scrolls, resizes, or moves it: a
  // change elsewhere on the page can move it without resizing it.
  useLayoutEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const box = target.getBoundingClientRect();
      setRect((last) => (last && last.top === box.top && last.left === box.left && last.width === box.width && last.height === box.height
        ? last
        : { top: box.top, left: box.left, width: box.width, height: box.height }));
    };
    const soon = () => { if (!frame) frame = window.requestAnimationFrame(measure); };
    measure();
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(soon) : null;
    resize?.observe(target);
    const moved = new MutationObserver(soon);
    moved.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
    window.addEventListener('scroll', soon, true);
    window.addEventListener('resize', soon);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      resize?.disconnect();
      moved.disconnect();
      window.removeEventListener('scroll', soon, true);
      window.removeEventListener('resize', soon);
    };
  }, [target]);

  // The box's own size: docked and placed it is a different width, so its
  // text wraps to a different height, and it is placed again for that.
  const [boxSize, setBoxSize] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(() => setBoxSize((last) => (
      last && last.width === box.offsetWidth && last.height === box.offsetHeight ? last : { width: box.offsetWidth, height: box.offsetHeight })));
    observer.observe(box);
    return () => observer.disconnect();
  }, [showing]);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || !rect) {
      setPlacement(null);
      return;
    }
    setPlacement(placeBox(rect, { width: box.offsetWidth, height: box.offsetHeight }, viewport));
  }, [rect, missing, done, step, viewport, boxSize]);

  // What the step waits for: the person's own click on the control, or an
  // event a real control raises, heard while the step is showing; or the page
  // it leads to (TOADO-988), reached however the person got there, a click, a
  // key or a link, shown or not. None is ever made by the
  // tour. A step that leads elsewhere moves on as it is met, since its Next is
  // about to be out of reach: from the handler, never from an effect watching
  // `done`, which on a new step still holds the step before's until the reset
  // above has run, and at most once.
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  useEffect(() => {
    const wait = current.waitFor;
    if (!wait || done) return;
    let met = false;
    // Never met outside the tour's own scope, or during a recording, however
    // it would be met: a page, an event, a click or a control already there.
    const inScope = !elsewhere && !recording && !scopePending;
    const satisfied = () => {
      if (met || !inScope) return;
      met = true;
      setDone(true);
      if (current.advanceWhenDone) onNextRef.current();
    };
    if ('route' in wait) {
      // A step that takes the person to its own page is met only once shown
      // there (Jason, 28 Oct): Back to it from the page it leads to renders
      // a moment before the page changes, and would be met and moved past.
      if (current.open && !shownAt) return;
      if (matchPath(wait.route, location.pathname)) satisfied();
      return;
    }
    if ('event' in wait && wait.present && anchorFor(wait.present)) {
      satisfied();
      return;
    }
    if (!showing) return;
    if ('event' in wait) {
      window.addEventListener(wait.event, satisfied);
      return () => window.removeEventListener(wait.event, satisfied);
    }
    if (!target) return;
    // The one control inside the step's that meets it, where the step names one
    // (Board, in the views), and never a click on what `ignore` matches, heard
    // as it lands, before the page has acted on it (the view already chosen).
    const meets = typeof wait.click === 'string' ? anchorFor(wait.click) : target;
    if (!meets) return;
    const ignore = wait.ignore;
    const clicked = (event: MouseEvent) => {
      if (!event.isTrusted) return;
      const path = event.composedPath();
      if (!path.includes(meets)) return;
      if (ignore && path.some((node) => node instanceof Element && node.matches(ignore))) return;
      satisfied();
    };
    document.addEventListener('click', clicked, true);
    return () => document.removeEventListener('click', clicked, true);
  }, [current.waitFor, current.advanceWhenDone, current.open, shownAt, done, target, showing, location.pathname, elsewhere, recording, scopePending]);

  // Focus on each step, so a keyboard or screen reader user meets it; never
  // over a modal, whose own focus it would fight (TOADO-988).
  useEffect(() => {
    // Asked of the page, not of `underModal`, which follows a modal a frame late.
    if (showing && !modalOpen()) boxRef.current?.focus({ preventScroll: true });
  }, [step, showing, underModal]);

  // Escape closes the tour when the key is the tour's: focus on its box, on
  // the control the step points at, on its pause, or nowhere in particular,
  // and nothing else used it. Whether anything did is known only once the
  // event has finished, every listener on the page in whatever order having
  // had its turn (a menu or popover closing, a search clearing), so the
  // decision waits for that, and is dropped if the tour has gone, or moved to
  // another control, meanwhile. Close is read through a ref: the provider
  // makes a new one on every render, and a render between the key and the
  // decision must not cancel it.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    let pending: number | undefined;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      // Over a modal the key is the modal's: only the tour's box, by its own handler, takes it there.
      if (event.key !== 'Escape' || modalOpen()) return;
      const focus = document.activeElement;
      const ours = !focus || focus === document.body
        || [boxRef.current, pausedRef.current, target].some((node) => !!node && node.contains(focus));
      if (!ours) return;
      window.clearTimeout(pending);
      pending = window.setTimeout(() => { if (!event.defaultPrevented) onCloseRef.current(); }, 0);
    };
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('keydown', onEscape);
      window.clearTimeout(pending);
    };
  }, [target]);

  // Focus the box had goes to the pause that replaced it, rather than to the page.
  useEffect(() => {
    if (showing || covered) return;
    const focus = document.activeElement;
    if (!focus || focus === document.body) pausedRef.current?.focus({ preventScroll: true });
  }, [showing, covered]);

  // In the box, Escape is the tour's before anything on the page hears it,
  // unless a modal has it: over a modal, only a step shown in it takes it.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || (modalOpen() && !current.overModal)) return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  const titleId = `tour-${tour.id}-title`;
  const last = position.index === position.total - 1;
  const count = `${position.index + 1} of ${position.total}`;

  // Under a modal: nothing at all, so nothing sits over it or takes its focus;
  // except, for a step in a window, the pause saying how to get back to it
  // (TOADO-988), which takes no focus from the window (the pause takes focus
  // only from nothing in particular).
  // A step that leads off a modal still says how to go on over it: the pause.
  if (scopePending) return null;
  if (covered && !behindAnother && !windowClosed && !cannotLoad && !(current.overModal && !onRoute)) return null;
  // The window the person is in, when the tour sits over one (review round 8).
  // From the watched state, not read during render, so a window closing over
  // another moves the copy to the one beneath (review round 9).
  const inWindow = underModal ? modal.top : null;
  if (!showing) {
    // Recording: said as such. Elsewhere: said as such. On another route:
    // shown before, it can be gone back to; never shown, the hint says where it carries on.
    const said = recording
      ? `${tour.title} is paused while a recording is under way.`
      : elsewhere
        ? `${tour.title} is paused: it was started in another ${elsewhere === 'project' ? 'project' : 'workspace'}.`
        : behindAnother
          ? 'Close the window on top to carry on with the tour.'
          // Before a closed window: a ticket that failed has no window to open.
          : cannotLoad ? 'This ticket could not be loaded, so this part of the tour cannot be shown.'
            : windowClosed ? current.reopenHint ?? 'The window this step is in is not open.'
          : shownAt || !current.routeHint ? `${tour.title} is paused here.` : current.routeHint;
    const skipPart = !elsewhere && !recording && current.section;
    // The paused box's buttons too are for a pointer only while the window has them (review round 10).
    const pausedTab = inWindow ? -1 : undefined;
    const closeTour = (tabIndex?: number) => (
      <Button size="sm" variant="ghost" iconOnly leftIcon={<X size={14} aria-hidden />} aria-label="Close tour" tabIndex={tabIndex} onClick={onClose} />
    );
    return (
      <>
        {inWindow && (
          <InWindow key={hostKey(inWindow)} host={inWindow} tourId={tour.id} text={`Tour paused: ${said}`} keyboard={keyboardInWindow}>
            {skipPart && <Button size="sm" variant="ghost" onClick={onSkipSection}>Skip this part</Button>}
            {closeTour()}
          </InWindow>
        )}
        {createPortal(
          // Said once: by the copy in the window when there is one (review round 9).
          <div ref={pausedRef} className={styles.paused} role={inWindow ? undefined : 'status'} tabIndex={-1} data-tour-paused={tour.id}>
            <span>{said}</span>
            {/* Never from a window's pause: the person is on the step's page, and
                going there again would open the ticket as a page and lose a draft. */}
            {/* Never shown here, the step's own page when the tour knows it (Jason, 28 Oct):
                resumed elsewhere, it used to say it was paused with no way on. */}
            {!elsewhere && !recording && !behindAnother && !windowClosed && (shownAt ?? (onRoute ? null : stepPage)) && (
              <Button size="sm" variant="secondary" tabIndex={pausedTab} onClick={() => navigate((shownAt ?? stepPage)!)}>Go back to the tour</Button>
            )}
            {/* A part of the tour that cannot be reached from here (no sample ticket to open, say) can be passed (TOADO-988). */}
            {skipPart && <Button size="sm" variant="ghost" tabIndex={pausedTab} onClick={onSkipSection}>Skip this part</Button>}
            {closeTour(pausedTab)}
          </div>,
          document.body,
        )}
      </>
    );
  }

  const style = placement && placement.side !== 'docked' ? { top: placement.top, left: placement.left } : undefined;
  // In the box and in the window alike, so the two cannot drift.
  const showHint = missing && !target;
  const stepBody = showHint
    // The hint says how to open a window, so only while none is open: with
    // one open, the control is missing from it for some other reason (review round 8).
    ? (!underModal && current.reopenHint) || 'This control is not on this page right now, so this step cannot be shown.'
    : current.body.replace('{selected}', selected ?? 'another view');
  const waiting = !done && !missing ? current.waitingHint ?? 'Do it on the page, or press Next and the tour does it for you.' : null;
  /**
   * Next (Jason, 28 Oct): on a step asking for a click that has not had one,
   * it makes that click, and the step moves on as it would from the person's
   * (a page or a window it opens, heard by the wait above; a click, here). On
   * a step already met, it clicks what closes the step's window, if it names
   * one, and moves on.
   */
  const next = () => {
    const wait = current.waitFor;
    if (wait && !done) {
      const named = current.nextClicks ?? ('click' in wait && typeof wait.click === 'string' ? wait.click : null);
      const control = (named ? anchorFor(named) : null) ?? target;
      if (!control) return;
      press(control);
      if ('click' in wait) {
        setDone(true);
        onNext();
      }
      return;
    }
    const closes = current.nextClicks ? anchorFor(current.nextClicks) : null;
    if (closes) press(closes);
    onNext();
  };
  const back = () => {
    const undoes = current.backClicks ? anchorFor(current.backClicks) : null;
    if (undoes) press(undoes);
    onBack();
  };
  // One keyboard path (review round 9): with the copy in the window, the box's
  // buttons are for a pointer only, so Tab meets each button once.
  const boxTab = inWindow ? -1 : undefined;
  // Skip for now always, on the left; on the right, Next, which does what the
  // step asks, or, only for a control that is not there, a skip past it.
  const actions = (tabIndex?: number) => (
    <>
      <Button size="sm" variant="ghost" tabIndex={tabIndex} onClick={onSkip}>Skip for now</Button>
      <span className={styles.spacer} />
      {canGoBack && <Button size="sm" variant="secondary" tabIndex={tabIndex} onClick={back}>Back</Button>}
      {missing && !target
        ? <Button size="sm" variant="secondary" tabIndex={tabIndex} onClick={current.section ? onSkipSection : onNext}>{last ? 'Finish' : current.section ? 'Skip this part' : 'Skip step'}</Button>
        : <Button size="sm" variant="primary" tabIndex={tabIndex} onClick={next}>{last ? 'Done' : 'Next'}</Button>}
    </>
  );
  const closeTour = (tabIndex?: number) => (
    <Button size="sm" variant="ghost" iconOnly leftIcon={<X size={14} aria-hidden />} aria-label="Close tour" tabIndex={tabIndex} onClick={onClose} />
  );
  return (
    <>
      {inWindow && (
        <InWindow key={hostKey(inWindow)} host={inWindow} tourId={tour.id} text={`Tour, step ${count}: ${current.title}. ${stepBody}${waiting ? ` ${waiting}` : ''}`} keyboard={keyboardInWindow}>
          {actions()}
          {/* Close keeps the step, where Skip for now starts over (review round 9). */}
          {closeTour()}
        </InWindow>
      )}
      {createPortal(
    <>
      {rect && (
        <div
          className={styles.ring}
          aria-hidden
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
        />
      )}
      <div
        ref={boxRef}
        className={styles.box}
        data-docked={!current.centered && (placement?.side === 'docked' || !rect) ? 'true' : undefined}
        data-centered={current.centered ? 'true' : undefined}
        style={current.centered ? undefined : style}
        role="dialog"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-tour-box={tour.id}
      >
        <div className={styles.head}>
          <span className={styles.count}>{count}</span>
          {closeTour(boxTab)}
        </div>
        {current.art && !missing && <TourArt name={current.art} />}
        <h2 id={titleId} className={styles.title}>{current.title}</h2>
        <p className={styles.body}>{stepBody}</p>
        {waiting && <p className={styles.waiting}>{waiting}</p>}
        <div className={styles.actions}>{actions(boxTab)}</div>
        {/* The recording bar's colours, running along the box's foot (Jason, 28 Oct). */}
        <span className={styles.glow} aria-hidden />
      </div>
    </>,
    document.body,
      )}
    </>
  );
}

/** A number per window, so the tour's copy inside one is a new one in the next. */
const hostKeys = new WeakMap<Element, number>();
let nextHostKey = 0;
function hostKey(host: Element): number {
  let key = hostKeys.get(host);
  if (key === undefined) {
    key = nextHostKey += 1;
    hostKeys.set(host, key);
  }
  return key;
}

/**
 * The step, and the tour's buttons, inside the window the tour sits over
 * (review round 8). A screen reader keeps to an `aria-modal` window, so the
 * box outside it, and its buttons, are not there for it; and the box cannot
 * sit inside one (it clips, and its transform moves a fixed box). So the
 * window gets its own copy: a status that says each step, and the same
 * buttons, drawn at the window's foot only while focus is among them.
 */
/** The button the keyboard was last on in the tour's copy inside a window, if it was (review rounds 10 and 11). */
type KeyboardInWindow = { element: Element | null; label: string | null };

/**
 * Where the copy is drawn while a keyboard is in it: the window's own bottom
 * right corner, measured (review round 11). Placed by the window's measured
 * box rather than by CSS alone, because what a fixed box is placed against
 * depends on the window: a transform makes the window that frame, and a
 * window whose opening animation is turned off for reduced motion has none,
 * so the same CSS put it in the screen's corner instead.
 */
type InWindowPlace = { right: number; bottom: number; width: number; maxHeight: number };

/** Set to something other than its resting value, for a style property an engine may not have (review rounds 12 and 13). */
function setTo(value: string | undefined): boolean {
  return value !== undefined && value !== '' && value !== 'none' && value !== 'normal';
}

/**
 * Whether this element is the box a fixed descendant is placed against: any
 * of the properties that make it so, the individual transforms and the
 * prefixed backdrop filter older WebKit has among them (review round 13).
 */
function framesFixed(style: CSSStyleDeclaration): boolean {
  const loose = style as CSSStyleDeclaration & { translate?: string; rotate?: string; scale?: string; webkitBackdropFilter?: string; containerType?: string };
  return setTo(style.transform) || setTo(style.filter) || setTo(style.perspective)
    || setTo(loose.translate) || setTo(loose.rotate) || setTo(loose.scale)
    || setTo(style.backdropFilter) || setTo(loose.webkitBackdropFilter)
    || /\b(layout|paint|strict|content)\b/.test(style.contain) || /\bsize\b/.test(loose.containerType ?? '')
    || /^(auto|hidden)$/.test((style as CSSStyleDeclaration & { contentVisibility?: string }).contentVisibility ?? '')
    || /transform|filter|translate|rotate|scale|perspective/.test(style.willChange);
}

/** The height of the copy's buttons with its padding: what it shows when nothing else fits. */
function buttonsNeed(group: HTMLElement): number {
  const actions = group.querySelector<HTMLElement>('[data-tour-actions]');
  const style = window.getComputedStyle(group);
  return (actions?.offsetHeight ?? 0) + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
}

function placeInWindow(host: Element, group: HTMLElement): InWindowPlace {
  const box = host.getBoundingClientRect();
  let frame: { right: number; bottom: number } = { right: window.innerWidth, bottom: window.innerHeight };
  for (let at = group.parentElement; at; at = at.parentElement) {
    if (framesFixed(window.getComputedStyle(at))) {
      frame = at.getBoundingClientRect();
      break;
    }
  }
  const visibleTop = Math.max(box.top, 0);
  const visibleBottom = Math.min(box.bottom, window.innerHeight);
  // Smaller margins on a window with little of it showing, so it all fits (review round 14).
  const gap = Math.min(12, Math.max(0, (visibleBottom - visibleTop) / 8));
  return {
    right: Math.max(0, frame.right - box.right + gap),
    bottom: Math.max(0, frame.bottom - visibleBottom + gap),
    width: Math.max(200, Math.min(340, box.width - 2 * gap)),
    // No taller than the part of the window on screen, so nothing of it is cut
    // off at the window's top on a short or zoomed screen; its words scroll
    // instead (review round 13), with no floor that would let it run past (round 14).
    // Never less than its buttons need, so the one with focus is never cut off (review round 15).
    maxHeight: Math.max(buttonsNeed(group), visibleBottom - visibleTop - 2 * gap),
  };
}

function InWindow({ host, tourId, text, keyboard, children }: {
  host: Element;
  tourId: string;
  text: string;
  keyboard: { current: KeyboardInWindow };
  children: ReactNode;
}) {
  // Mounted empty and then filled: a live region that arrives already holding
  // its words is often not read out (review round 8).
  const [said, setSaid] = useState('');
  useEffect(() => setSaid(text), [text]);
  const groupRef = useRef<HTMLDivElement>(null);
  const wordsRef = useRef<HTMLParagraphElement>(null);
  // Each step's words start at their top, however far the last step's were scrolled (review round 15).
  useLayoutEffect(() => {
    if (wordsRef.current) wordsRef.current.scrollTop = 0;
  }, [said]);
  // Focus that fell to the page because its button went goes to the same
  // button here, or to Close, which keeps the step (review round 10). Only
  // when that button is gone: focus the person moved away is theirs (review round 11).
  useLayoutEffect(() => {
    const active = document.activeElement;
    const last = keyboard.current.element;
    if (!last || last.isConnected || (active && active !== document.body)) return;
    const buttons = [...(groupRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    const same = buttons.find((button) => (button.getAttribute('aria-label') ?? button.textContent) === keyboard.current.label);
    (same ?? buttons.at(-1))?.focus({ preventScroll: true });
  });
  const [placed, setPlaced] = useState<InWindowPlace | null>(null);
  const [focused, setFocused] = useState(false);
  useLayoutEffect(() => {
    if (!focused || !groupRef.current) {
      setPlaced(null);
      return;
    }
    const group = groupRef.current;
    // Only a change re-renders: its own words scrolling are heard here too (review round 14).
    const place = () => {
      const next = placeInWindow(host, group);
      setPlaced((last) => (last && last.right === next.right && last.bottom === next.bottom
        && last.width === next.width && last.maxHeight === next.maxHeight ? last : next));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    /**
     * And whenever what it is measured against can have moved without the
     * screen changing (review round 12): the window resizing itself, its
     * opening animation or a transition ending, and the motion preference
     * changing, which takes the window's transform away or gives it back.
     */
    const resized = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
    resized?.observe(host);
    // Anywhere, not on the window alone: the box it is placed against can be an
    // ancestor, whose events never reach the window, and an animation cut short
    // ends with a cancel, not an end (review round 13).
    const settled = ['animationend', 'animationcancel', 'transitionend', 'transitioncancel'] as const;
    for (const type of settled) document.addEventListener(type, place, true);
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    motion?.addEventListener?.('change', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      resized?.disconnect();
      for (const type of settled) document.removeEventListener(type, place, true);
      motion?.removeEventListener?.('change', place);
    };
  }, [focused, host]);
  return createPortal(
    <div
      ref={groupRef}
      className={styles.inWindow}
      style={focused && placed ? placed : undefined}
      role="group"
      aria-label="Tour"
      data-tour-said={tourId}
      onFocus={() => setFocused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}
    >
      {/* Focusable, so words that scroll can be scrolled from the keyboard (review round 15). */}
      <p ref={wordsRef} role="status" className={styles.inWindowText} tabIndex={0}>{said}</p>
      <div className={styles.actions} data-tour-actions>{children}</div>
    </div>,
    host,
  );
}
